import React, { useState, useEffect } from 'react';
import { Building2, ChevronDown, Check, Plus, Users, LogOut } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuLabel,
} from './ui/dropdown-menu';
import { Badge } from './ui/badge';
import { toast } from 'sonner';
import api from '../utils/api';
import { useAuth } from '../context/AuthContext';

const TenantSwitcher = () => {
  const { user, login } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [currentTenantId, setCurrentTenantId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState([]);

  useEffect(() => {
    fetchTenants();
    fetchInvitations();
  }, []);

  const fetchTenants = async () => {
    try {
      const response = await api.get('/tenant-memberships');
      setTenants(response.data.tenants || []);
      setCurrentTenantId(response.data.current_tenant_id);
    } catch (error) {
      console.error('Failed to fetch tenants:', error);
    }
  };

  const fetchInvitations = async () => {
    try {
      const response = await api.get('/tenant-memberships/invitations');
      setPendingInvitations(response.data.invitations || []);
    } catch (error) {
      console.error('Failed to fetch invitations:', error);
    }
  };

  const handleSwitchTenant = async (tenantId) => {
    if (tenantId === currentTenantId) return;
    
    setLoading(true);
    try {
      const response = await api.post(`/tenant-memberships/switch/${tenantId}`);
      
      // Update the auth token
      const { access_token, tenant_name, role } = response.data;
      
      // Store the new token
      localStorage.setItem('token', access_token);
      
      // Update auth context with new user data
      const userResponse = await api.get('/auth/me');
      login(access_token, userResponse.data);
      
      toast.success(`Switched to ${tenant_name}`);
      setCurrentTenantId(tenantId);
      
      // Refresh the page to load new tenant data
      window.location.reload();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to switch organization');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvitation = async (membershipId) => {
    try {
      const response = await api.post(`/tenant-memberships/invitations/${membershipId}/accept`);
      toast.success(response.data.message);
      fetchTenants();
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept invitation');
    }
  };

  const handleDeclineInvitation = async (membershipId) => {
    try {
      await api.post(`/tenant-memberships/invitations/${membershipId}/decline`);
      toast.success('Invitation declined');
      fetchInvitations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to decline invitation');
    }
  };

  const currentTenant = tenants.find(t => t.tenant_id === currentTenantId);
  const otherTenants = tenants.filter(t => t.tenant_id !== currentTenantId);

  // Don't show switcher if user only has one tenant and no invitations
  if (tenants.length <= 1 && pendingInvitations.length === 0) {
    return null;
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button 
          variant="ghost" 
          className="flex items-center gap-2 px-3 py-2 h-auto"
          disabled={loading}
          data-testid="tenant-switcher"
        >
          <Building2 size={16} className="text-slate-500" />
          <div className="flex flex-col items-start">
            <span className="text-sm font-medium truncate max-w-[150px]">
              {currentTenant?.tenant_name || 'My Organization'}
            </span>
            {currentTenant && !currentTenant.is_primary && (
              <span className="text-xs text-slate-400">Guest Access</span>
            )}
          </div>
          <ChevronDown size={14} className="text-slate-400" />
          {pendingInvitations.length > 0 && (
            <Badge variant="destructive" className="h-5 w-5 p-0 flex items-center justify-center text-xs">
              {pendingInvitations.length}
            </Badge>
          )}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wide">
          Organizations
        </DropdownMenuLabel>
        
        {/* Current Tenant */}
        {currentTenant && (
          <DropdownMenuItem 
            className="flex items-center justify-between py-3 cursor-default bg-slate-50"
            data-testid="current-tenant"
          >
            <div className="flex items-center gap-2">
              <Building2 size={16} className="text-indigo-600" />
              <div>
                <p className="font-medium">{currentTenant.tenant_name}</p>
                <p className="text-xs text-slate-500 capitalize">{currentTenant.role}</p>
              </div>
            </div>
            <Check size={16} className="text-indigo-600" />
          </DropdownMenuItem>
        )}
        
        {/* Other Tenants */}
        {otherTenants.length > 0 && (
          <>
            <DropdownMenuSeparator />
            {otherTenants.map((tenant) => (
              <DropdownMenuItem
                key={tenant.tenant_id}
                onClick={() => handleSwitchTenant(tenant.tenant_id)}
                className="flex items-center justify-between py-3 cursor-pointer"
                data-testid={`tenant-${tenant.tenant_id}`}
              >
                <div className="flex items-center gap-2">
                  <Building2 size={16} className="text-slate-400" />
                  <div>
                    <p className="font-medium">{tenant.tenant_name}</p>
                    <p className="text-xs text-slate-500 capitalize">
                      {tenant.role}
                      {!tenant.is_primary && ' • Guest'}
                    </p>
                  </div>
                </div>
              </DropdownMenuItem>
            ))}
          </>
        )}
        
        {/* Pending Invitations */}
        {pendingInvitations.length > 0 && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-slate-500 uppercase tracking-wide">
              Pending Invitations
            </DropdownMenuLabel>
            {pendingInvitations.map((invitation) => (
              <div 
                key={invitation.membership_id}
                className="px-2 py-3 border-b last:border-0"
              >
                <div className="flex items-center gap-2 mb-2">
                  <Users size={14} className="text-amber-500" />
                  <div>
                    <p className="font-medium text-sm">{invitation.tenant_name}</p>
                    <p className="text-xs text-slate-500">
                      Invited by {invitation.invited_by_name} • {invitation.role}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button 
                    size="sm" 
                    variant="outline" 
                    className="flex-1 h-7 text-xs"
                    onClick={() => handleDeclineInvitation(invitation.membership_id)}
                  >
                    Decline
                  </Button>
                  <Button 
                    size="sm" 
                    className="flex-1 h-7 text-xs"
                    onClick={() => handleAcceptInvitation(invitation.membership_id)}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default TenantSwitcher;
